import { MutableRefObject, memo, useMemo } from 'react';
import { VirtualItem } from '@tanstack/react-virtual';

import { DbIdsPerModel } from '../common/ForgeViewer';
import { ViewerMode } from '../common/viewer-mode';
import ShapeItem, { ShapeNodeType } from './ShapeItem';
import { isShapeHidden, isShapeSelected } from './ShapesPanel';
import {
  AutoShapeDeepFragment,
  ShapeDeepFragment,
  ShapeFolderDeepFragment,
  SheetShapeDeepFragment,
} from 'src/gql/graphql';

const SHAPE_ITEM_SIZE = 26;

interface ShapeFolderProps {
  sortedFolders: (AutoShapeDeepFragment | ShapeFolderDeepFragment)[];
  virtualItem: VirtualItem;
  virtualItems: VirtualItem[];
  shapesIndexedByFolder: Map<
    string,
    (ShapeDeepFragment | SheetShapeDeepFragment)[]
  >;
  hiddenBIMElements: DbIdsPerModel;
  hiddenSheetElements: DbIdsPerModel;
  expandedFolders: Record<string, boolean>;
  selectedFolder: string | null;
  toggleFolderExpansion: (folderId: string) => void;
  selectedShapes: (ShapeDeepFragment | SheetShapeDeepFragment)[];
  handleNodeSelect: (node: ShapeNodeType) => void;
  handleShapeMove: (
    draggedNodeIds: (ShapeDeepFragment | SheetShapeDeepFragment)[],
    targetNode: ShapeFolderDeepFragment | null
  ) => void;
  handleRename: (node: ShapeNodeType, newName: string) => void;
  handleShow: (node: ShapeNodeType) => void;
  handleHide: (node: ShapeNodeType) => void;
  onDeleteHandler: (node: ShapeNodeType) => void;
  setPageNumber: (pageNumber: number) => void;
  changeActiveSheet: (sheetId: string) => void;
  edit3dShape: (shapeToEdit: string) => void;
  editSheetShape: (shapeToEdit: string) => void;
  setViewerMode: (mode: ViewerMode) => void;
  ungroupShapesFromFolder: (node: ShapeNodeType) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  groupShapesIntoFolder: () => Promise<void>;
  selectedShapesRef: MutableRefObject<
    (ShapeDeepFragment | SheetShapeDeepFragment)[]
  >;
  selectedDbIds: Record<string, number[]>;
  isDiffing?: boolean;
  getShapeDiff: (shape: ShapeDeepFragment | SheetShapeDeepFragment) => any;
  getFolderDiff: (
    folder: ShapeFolderDeepFragment | AutoShapeDeepFragment
  ) => any;
}

function ShapeFolder({
  sortedFolders,
  virtualItem,
  shapesIndexedByFolder,
  virtualItems,
  hiddenBIMElements,
  hiddenSheetElements,
  expandedFolders,
  selectedFolder,
  selectedShapes,
  toggleFolderExpansion,
  handleRename,
  handleHide,
  handleShow,
  handleShapeMove,
  handleNodeSelect,
  onDeleteHandler,
  changeActiveSheet,
  edit3dShape,
  editSheetShape,
  setPageNumber,
  setViewerMode,
  ungroupShapesFromFolder,
  deleteFolder,
  groupShapesIntoFolder,
  selectedShapesRef,
  selectedDbIds,
  isDiffing,
  getShapeDiff,
  getFolderDiff,
}: ShapeFolderProps) {
  const hiddenElements = useMemo(() => {
    return { ...hiddenBIMElements, ...hiddenSheetElements };
  }, [hiddenBIMElements, hiddenSheetElements]);

  const folder = useMemo(
    () => sortedFolders.find((f) => f.id === virtualItem.key),
    [sortedFolders]
  );

  const rawFolderShapes = useMemo(
    () => shapesIndexedByFolder.get(folder!.id) || [],
    [shapesIndexedByFolder]
  );

  // Get the folderShapes indexed by the virtualizer
  const folderShapes = useMemo(
    () =>
      rawFolderShapes.filter((rShape) =>
        virtualItems.find((vItem) => vItem.key === rShape.id)
      ),
    [rawFolderShapes, virtualItems]
  );

  const allChildrenHidden =
    rawFolderShapes.length > 0 &&
    rawFolderShapes.every((shape) =>
      Object.keys(hiddenElements).some(
        (urn) => urn === shape.urn && hiddenElements[urn].includes(shape.dbId)
      )
    );

  const shapeChildren = useMemo(() => {
    return folderShapes.map((childShape) => {
      const shapeVirtualItem = virtualItems.find(
        (vi) => vi.key === childShape.id
      );
      return {
        key: childShape.id,
        node: childShape,
        isSelected: isShapeSelected(childShape, selectedDbIds),
        isHidden: isShapeHidden(
          childShape,
          hiddenBIMElements,
          hiddenSheetElements
        ),
        onShapeSelect: handleNodeSelect,
        onMoveNode: handleShapeMove,
        onRename: handleRename,
        onShow: handleShow,
        onHide: handleHide,
        onDelete: onDeleteHandler,
        changeActiveSheet,
        editBIMShape: edit3dShape,
        setPageNumber,
        editSheetShape,
        setViewerMode,
        removeShapesFromFolder: ungroupShapesFromFolder,
        deleteFolder,
        groupShapesIntoFolder,
        selectedShapesRef,
        top: shapeVirtualItem!.start - virtualItem.start - SHAPE_ITEM_SIZE,
        height: shapeVirtualItem!.size,
        isDiffing,
        diff: getShapeDiff(childShape),
      };
    });
  }, [
    folderShapes,
    virtualItems,
    selectedDbIds,
    hiddenBIMElements,
    hiddenSheetElements,
    handleNodeSelect,
    handleShapeMove,
    handleRename,
    handleShow,
    handleHide,
    onDeleteHandler,
    changeActiveSheet,
    edit3dShape,
    setPageNumber,
    editSheetShape,
    setViewerMode,
    ungroupShapesFromFolder,
    deleteFolder,
    groupShapesIntoFolder,
    isShapeHidden,
    isShapeSelected,
    isDiffing,
    getShapeDiff,
    getFolderDiff,
  ]);

  if (!folder) return null;

  return (
    <ShapeItem
      node={folder}
      expanded={expandedFolders[folder.id]}
      setExpanded={toggleFolderExpansion}
      isSelected={selectedFolder === folder.id}
      allChildrenSelected={
        rawFolderShapes.length > 0 &&
        rawFolderShapes.every((shape) =>
          selectedShapes.some((s) => s.id === shape.id && s.urn === shape.urn)
        )
      }
      allChildrenHidden={allChildrenHidden}
      onShapeSelect={handleNodeSelect}
      onMoveNode={handleShapeMove}
      onRename={handleRename}
      onShow={handleShow}
      onHide={handleHide}
      onDelete={onDeleteHandler}
      isHidden={isShapeHidden(
        folder,
        hiddenBIMElements,
        hiddenBIMElements,
        allChildrenHidden
      )}
      changeActiveSheet={changeActiveSheet}
      editBIMShape={edit3dShape}
      setPageNumber={setPageNumber}
      editSheetShape={editSheetShape}
      setViewerMode={setViewerMode}
      removeShapesFromFolder={ungroupShapesFromFolder}
      deleteFolder={deleteFolder}
      groupShapesIntoFolder={groupShapesIntoFolder}
      selectedShapesRef={selectedShapesRef}
      hasChildren={rawFolderShapes.length > 0}
      top={virtualItem.start}
      height={virtualItem.size}
      shapeChildren={shapeChildren}
      isDiffing={isDiffing}
      diff={getFolderDiff(folder)}
    />
  );
}

export default memo(ShapeFolder);
